import React from 'react';
import { z } from 'zod';
import { safeUrl } from '../url';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Enquiry cards — Strapi sections.enquiry-section + components.enquiry-card +
// atoms.info. A grid of contact cards, each with a title, description, and info
// rows (label + value linking via url, e.g. mailto:/tel:).
export const enquirySectionSchema = z.object({
  cards: z
    .array(
      z.object({
        title: z.string().min(1),
        description: z.string().optional(),
        info: z
          .array(
            z.object({
              iconUrl: z.string().optional(),
              label: z.string().optional(),
              value: z.string().min(1),
              url: safeUrl.optional(),
            }),
          )
          .default([]),
      }),
    )
    .min(1),
});

type EnquirySectionProps = z.infer<typeof enquirySectionSchema>;

const Section = styled.section`
  max-width: 120rem;
  margin: 0 auto;
  padding: 4rem 1.6rem 6rem;
`;

const Grid = styled.div`
  display: grid;
  gap: 2.4rem;
  grid-template-columns: 1fr;

  @media (min-width: 48em) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 64em) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Card = styled.div`
  padding: 2.4rem;
  border: 1px solid #e8efff;
  border-radius: 1.6rem;
  background: #ffffff;
`;

const CardTitle = styled.h3`
  margin: 0;
  color: #0b1020;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 2rem;
`;

const CardDesc = styled.p`
  margin: 0.4rem 0 1.6rem;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.4rem;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 0.6rem 0;
`;

const Icon = styled.img`
  width: 2rem;
  height: 2rem;
  object-fit: contain;
  flex: 0 0 auto;
`;

const InfoLabel = styled.span`
  color: #7a7f8c;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.2rem;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const InfoValue = styled.a`
  color: #063091;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const InfoValuePlain = styled.span`
  color: #2b2f3a;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
`;

function EnquirySection({ cards }: EnquirySectionProps) {
  return (
    <Section>
      <Grid>
        {cards.map((card, i) => (
          <Card key={`${card.title}-${i}`}>
            <CardTitle>{card.title}</CardTitle>
            {card.description ? <CardDesc>{card.description}</CardDesc> : null}
            {card.info.map((row, j) => (
              <InfoRow key={j}>
                {row.iconUrl ? <Icon src={row.iconUrl} alt="" /> : null}
                <span>
                  {row.label ? (
                    <>
                      <InfoLabel>{row.label}</InfoLabel>
                      <br />
                    </>
                  ) : null}
                  {row.url ? (
                    <InfoValue href={row.url}>{row.value}</InfoValue>
                  ) : (
                    <InfoValuePlain>{row.value}</InfoValuePlain>
                  )}
                </span>
              </InfoRow>
            ))}
          </Card>
        ))}
      </Grid>
    </Section>
  );
}

export const enquirySectionBlock: BlockDefinition<EnquirySectionProps> = {
  type: 'enquirySection',
  schema: enquirySectionSchema,
  Renderer: EnquirySection,
};
